import Help from "@features/business/components/help";
import esgImage from "@assets/images/esg-team.png";
import carbonImage from "@assets/images/carbon-technician.png";
import documentIcon from "@assets/icons/document.svg";
import downloadIconRed from "@assets/icons/file-download-fill-red.svg";
import downloadIcon from "@assets/icons/download-arrow.svg";
import gisLogo from "@assets/images/gaz-logo-color.svg";
import {
  governanceFrameworkCategories,
  certificationsLicenses,
  complianceStructureColumns,
  riskManagementColumns,
  governanceDownloads,
  contractorStructureColumns,
} from "@features/business/data";

interface CorporateGovernancePageProps {
  hero: React.ReactNode;
  footer?: React.ReactNode;
}

export default function CorporateGovernancePage({
  hero,
  footer,
}: CorporateGovernancePageProps) {
  return (
    <div className="w-full flex flex-col bg-white">
      {hero}

      {/* ── CORPORATE GOVERNANCE FRAMEWORK ────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Corporate Governance Framework
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Our governance structure is based on four core pillars:
          </p>

          <div className="grid gap-4 mt-5 lg:mt-16 md:grid-cols-2">
            {governanceFrameworkCategories.map((cat, index) => (
              <article
                key={index}
                className="p-4 md:p-8 border border-[#62636C33] rounded-[12px] md:rounded-[24px] bg-white"
              >
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl">
                  {cat.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF] mt-2">
                  {cat.subtitle}
                </p>

                <ul className="mt-3 space-y-2">
                  {cat.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                      <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CERTIFICATIONS & LICENSES ──────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Certifications &amp; Licenses
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Our comprehensive certifications demonstrate our commitment to
            excellence.
          </p>

          <div className="grid gap-4 mt-5 lg:mt-16 md:grid-cols-2">
            {certificationsLicenses.map((cert, index) => (
              <article
                key={index}
                className="p-4 md:p-8 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px] flex flex-col"
              >
                <div className="flex items-center gap-3">
                  <div className="bg-black size-9 md:size-[56px] grid place-content-center rounded-[12px] flex-shrink-0">
                    <span className="text-xs font-semibold text-white md:text-base lg:text-lg">
                      {`0${index + 1}`}
                    </span>
                  </div>
                  <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl leading-snug">
                    {cert.title}
                  </h3>
                </div>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF] mt-3">
                  {cert.subtitle}
                </p>
                <ul className="mt-3 space-y-2">
                  {cert.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                      <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
                <button className="flex items-center gap-2 pt-4 mt-auto text-xs font-medium text-gis-red md:text-sm lg:text-lg">
                  Download Certificate
                  <img
                    src={downloadIconRed}
                    alt="download"
                    className="size-4 md:size-5 lg:size-6"
                  />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── REGULATORY & SAFETY COMPLIANCE STRUCTURE ──────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Regulatory &amp; Safety Compliance Structure
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            A comprehensive approach to maintaining the highest standards.
          </p>

          <div className="grid gap-4 mt-5 lg:mt-16 md:grid-cols-2 lg:grid-cols-3">
            {complianceStructureColumns.map((col, index) => (
              <article
                key={index}
                className="p-4 md:p-6 border border-[#62636C33] rounded-[12px] md:rounded-[24px] bg-white"
              >
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl">
                  {col.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF] mt-2">
                  {col.subtitle}
                </p>
                <ul className="mt-2 space-y-2">
                  {col.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                      <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── CONTRACTOR & SUPPLIER MANAGEMENT ──────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Contractor &amp; Supplier Management
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Strict controls ensuring quality and compliance throughout our
            supply chain.
          </p>

          <div className="grid gap-4 mt-5 lg:mt-16 md:grid-cols-2">
            {contractorStructureColumns.map((col, index) => (
              <article
                key={index}
                className="p-4 md:p-6 border border-[#62636C33] rounded-[12px] md:rounded-[24px] bg-white"
              >
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl">
                  {col.title}
                </h3>
                <p className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF] mt-2">
                  {col.subtitle}
                </p>
                <ul className="mt-2 space-y-2">
                  {col.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-[#62636C]" />
                      <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── ENVIRONMENTAL & SUSTAINABILITY ────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Environmental &amp; Sustainability
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 lg:mt-4">
            Our commitment to a sustainable future.
          </p>

          {/* ESG */}
          <div className="flex flex-col items-start justify-between gap-10 mt-10 xl:flex-row lg:mt-16">
            <div className="xl:max-w-[560px]">
              <h3 className="text-gis-dark-blue text-[20px] md:text-[32px] lg:text-[40px] font-semibold">
                Environmental &amp; Sustainability Commitment (ESG)
              </h3>
              <p className="mt-3 text-[14px] md:text-base text-[#62636C]">
                Our ESG strategy:
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  "Reducing gas leaks through smart metering",
                  "Promoting energy-efficient appliances",
                  "Minimizing waste during installation",
                  "Regular monitoring of emissions",
                  "Community education on safe & sustainable energy use",
                ].map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 md:mt-3 size-2 md:size-3 rounded-full bg-[#62636C]" />
                    <span className="text-[14px] md:text-base text-[#62636C]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:max-w-[580px] w-full flex-shrink-0">
              <img
                src={esgImage}
                alt="ESG team"
                className="w-full rounded-[12px] md:rounded-[24px] object-cover"
              />
            </div>
          </div>

          {/* Carbon Footprint */}
          <div className="flex flex-col-reverse items-start justify-between gap-10 mt-10 xl:flex-row lg:mt-20 xl:items-center">
            <div className="md:max-w-[500px] w-full flex-shrink-0">
              <img
                src={carbonImage}
                alt="carbon reduction technician"
                className="w-full rounded-[12px] md:rounded-[24px] object-cover"
              />
            </div>
            <div className="xl:max-w-[640px]">
              <h3 className="text-gis-dark-blue text-[20px] md:text-[32px] lg:text-[40px] font-semibold">
                Carbon Footprint Reduction Efforts
              </h3>
              <p className="mt-3 text-[14px] md:text-base text-[#62636C]">
                Our ongoing efforts to minimize environmental impact:
              </p>
              <ul className="mt-4 space-y-3">
                {[
                  "Transition toward digital metering",
                  "Paperless billing",
                  "Efficient fleet routing for technicians",
                ].map((point, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <span className="flex-shrink-0 mt-1.5 md:mt-3 size-2 md:size-3 rounded-full bg-[#62636C]" />
                    <span className="text-[14px] md:text-base text-[#62636C]">
                      {point}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── RISK MANAGEMENT FRAMEWORK ─────────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Risk Management Framework
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            GAZ employs a proactive risk framework based on company-wide
            assessments.
          </p>

          <div className="grid gap-4 mt-5 lg:mt-16 md:grid-cols-3">
            {riskManagementColumns.map((col, index) => (
              <article
                key={index}
                className="p-4 md:p-6 bg-white border border-[#62636C33] rounded-[12px] md:rounded-[24px]"
              >
                <h3 className="text-[#090D2C] font-semibold text-sm md:text-base lg:text-xl mb-3 md:mb-4">
                  {col.title}
                </h3>
                <ul className="space-y-2">
                  {col.points.map((point, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span className="flex-shrink-0 mt-1.5 size-1.5 md:size-2 rounded-full bg-gis-red" />
                      <span className="text-[14px] md:text-xs lg:text-sm text-[#0A0E2EBF]">
                        {point}
                      </span>
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── MESSAGE FROM THE MANAGEMENT ───────────────────────────────── */}
      <section className="py-10 md:py-20 lg:py-[120px] px-5 md:px-10">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[22px] md:text-[32px] lg:text-[48px] text-center font-semibold">
            Message from the Management
          </h2>
          <p className="mt-6 text-[14px] md:text-base lg:text-xl 2xl:text-[40px] text-[#62636C] leading-relaxed text-center">
            At GAZ, safety and compliance are not tasks they are values
            embedded into every decision we make. Our commitment to ethics,
            regulatory excellence, and transparent operations ensures safe
            environments for our customers and our communities.
          </p>
          <div className="flex items-center justify-center gap-3 mt-8 md:mt-16">
            <img
              src={gisLogo}
              alt="GIS logo"
              className="flex-shrink-0 h-[60px] md:h-20 lg:h-[105px]"
            />
            <div className="text-left text-gis-dark-blue">
              <p className="text-sm font-semibold md:text-base lg:text-[40px]">
                GIS Management
              </p>
              <p className="text-xs md:text-sm lg:text-2xl">
                Gaz Investment in Oil and Gas Supply L.L.C.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── DOWNLOADS ─────────────────────────────────────────────────── */}
      <section className="py-10 md:py-16 px-5 md:px-10 bg-[#F1F1F1]">
        <div className="w-[1368px] max-w-full mx-auto">
          <h2 className="text-gis-dark-blue text-[24px] md:text-[40px] lg:text-[56px] font-semibold text-center">
            Downloads
          </h2>
          <p className="text-center text-[14px] md:text-base lg:text-lg text-[#62636C] mt-2 md:mt-4">
            Access our official documentation and certificates.
          </p>

          <div className="grid gap-4 mt-10 lg:grid-cols-2 md:mt-16">
            {governanceDownloads.map((resource, index) => (
              <article
                key={index}
                className="bg-white p-4 md:p-6 rounded-[12px] md:rounded-[24px] flex items-center justify-between gap-4"
              >
                <div className="flex flex-row items-center gap-4 xl:gap-8">
                  <div className="bg-[#F1F1F1] p-2 rounded-[12px] flex-shrink-0">
                    <img
                      src={documentIcon}
                      alt="document icon"
                      className="size-5 md:size-12"
                    />
                  </div>
                  <h4 className="text-[#1E1F24] font-normal text-xs md:text-base lg:text-xl">
                    {resource}
                  </h4>
                </div>
                <button className="flex-shrink-0 size-8 md:size-10 hover:bg-[#F1F1F1] border border-[#F1F1F1] rounded-full grid place-content-center">
                  <img
                    src={downloadIcon}
                    alt="download icon"
                    className="size-5 md:size-7 invert"
                  />
                </button>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── HELP CTA ───────────────────────────────────────────────────── */}
      <Help />

      {footer}
    </div>
  );
}
